import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CONFIRMATION_WORD = "Delete";

type DeleteConfirmModalProps = {
    open: boolean;
    title: string;
    message: React.ReactNode;
    onConfirm: () => Promise<string>;
    onClose: (wasDeleted: boolean) => void;
};

export default function DeleteConfirmModal({
    open,
    title,
    message,
    onConfirm,
    onClose,
}: DeleteConfirmModalProps) {
    const [typedWord, setTypedWord] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [result, setResult] = useState<{ error: string } | { error: null } | null>(null);

    useEffect(() => {
        if (!open) return;
        setTypedWord("");
        setIsDeleting(false);
        setResult(null);
    }, [open]);

    const canConfirm =
        typedWord.trim().toLowerCase() === CONFIRMATION_WORD.toLowerCase() && !isDeleting;
    const wasDeleted = result?.error === null;

    const handleConfirm = async () => {
        setIsDeleting(true);
        const error = await onConfirm();
        setIsDeleting(false);
        setResult({ error: error || null });
    };

    const handleClose = () => {
        if (isDeleting) return;
        onClose(wasDeleted);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {title}
                <IconButton aria-label="close" onClick={handleClose} disabled={isDeleting} sx={{ p: 0, color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {result ? (
                    <p className={`delete-confirm__result delete-confirm__result--${wasDeleted ? "success" : "error"}`}>
                        {wasDeleted ? "Deleted successfully." : result.error}
                    </p>
                ) : (
                    <>
                        <p className="delete-confirm__message">{message}</p>

                        <label className="delete-confirm__label" htmlFor="delete-confirmation">
                            Type <strong>{CONFIRMATION_WORD}</strong> to confirm
                        </label>
                        <input
                            id="delete-confirmation" className="delete-confirm__input" type="text"
                            value={typedWord} onChange={(event) => setTypedWord(event.target.value)}
                            disabled={isDeleting} autoComplete="off"
                        />

                        {isDeleting && (
                            <p className="delete-confirm__progress">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Deleting…
                            </p>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                {result ? (
                    <Button onClick={handleClose} variant="contained" color="inherit">
                        Close
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} variant="outlined" color="inherit" disabled={isDeleting}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirm} variant="contained" color="error" disabled={!canConfirm}>
                            {isDeleting ? "Deleting…" : CONFIRMATION_WORD}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
